"use client";
import { createContext, useContext, ReactNode, useEffect, useRef, useState, Key, useCallback } from "react";
import { initializeApp } from "firebase/app";
import { getAnalytics, Analytics, isSupported, logEvent } from "firebase/analytics";
import { useLocale } from "@react-aria/i18n";
import { usePathname } from "@/i18n/routing";
import firebaseConfig from "@/lib/firebase.config";

interface FirebaseProviderProps {
  analytics: Analytics | null;
  pushEvent: (key: Key, params: Record<string, any>) => void;
}
const FirebaseContext = createContext<FirebaseProviderProps | undefined>({ analytics: null, pushEvent: () => {} });

export function FirebaseProvider({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const pathname = usePathname();
  const [analytics, setAnalytics] = useState<Analytics | null>(null);

  const pushEvent = useCallback(
    (key: Key, params: Record<string, any>) => {
      if (analytics) {
        logEvent(analytics, String(key), params);
      }
    },
    [analytics]
  );

  // useEffect(() => {
  //   pushEvent("next_pathname", {
  //     locale,
  //     pathname,
  //   });
  // }, [locale, pathname]);

  useEffect(() => {
    const app = initializeApp(firebaseConfig);
    // make sure analytics is supported (avoids SSR/unsupported browser issues)
    isSupported().then((yes) => {
      if (yes) {
        const analyticsInstance = getAnalytics(app);
        setAnalytics(analyticsInstance);
      }
    });
  }, []);

  return <FirebaseContext.Provider value={{ analytics, pushEvent }}>{children}</FirebaseContext.Provider>;
}

export function useFirebase() {
  const context = useContext(FirebaseContext);
  if (context === undefined) {
    throw new Error("useFirebase must be used within a FirebaseProvider");
  }
  return context;
}
